package com.bikompanion.data.parking

import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import com.bikompanion.data.logNetwork
import com.bikompanion.domain.BikeParkingSpot
import com.bikompanion.domain.BoundingBox
import com.bikompanion.domain.ParkingCell
import com.bikompanion.domain.ParkingRepository

/**
 * Serves bike racks out of the dataset shipped with the app, anywhere in the world.
 *
 * This is the whole answer to why the rack layer used to be unusable. It asked Overpass, live,
 * for whatever the camera was over. Overpass is volunteer infrastructure with about two request
 * slots per address; its own wiki calls the main instance overloaded and asks a regular app to
 * stay under roughly a hundred queries a day; and on a measured afternoon two of the four public
 * mirrors were down outright. A map layer cannot be built on that.
 *
 * OpenStreetMap has 926,356 bike parkings worldwide, which packs into 5.5 MB — small enough that
 * the right move is to carry all of it. Every lookup is then local: no request, no rate limit, no
 * mirror, no spinner, and it works offline.
 *
 * [fallback] covers the one case the file cannot: a build where the resource is missing or
 * unreadable. It is not a freshness mechanism — the dataset is refreshed by rebuilding it, in
 * tools/parking-dataset.
 */
internal class BundledParkingRepository(
    private val fallback: ParkingRepository,
    private val loadBytes: suspend () -> ByteArray? = ::loadParkingDatasetBytes,
) : ParkingRepository {
    private val lock = Mutex()
    private var dataset: BundledParkingDataset? = null
    private var opened = false

    override suspend fun fetchBikeParking(bounds: BoundingBox): List<BikeParkingSpot> {
        val dataset = dataset() ?: return fallback.fetchBikeParking(bounds)
        // Whole cells, because that is what the file is indexed by and what the caller asks in.
        return ParkingCell.covering(bounds).flatMap(dataset::racksIn)
    }

    /**
     * Opens the file once and keeps it, including when it turns out not to be there.
     *
     * Kept rather than re-read because it is 5.5 MB: reading it per camera move would cost more
     * than the Overpass request this replaced. Remembering the failure matters just as much —
     * without [opened] a build with no dataset would re-read nothing on every single lookup.
     */
    private suspend fun dataset(): BundledParkingDataset? =
        lock.withLock {
            if (!opened) {
                opened = true
                val bytes = loadBytes()
                dataset = bytes?.let(BundledParkingDataset::openOrNull)
                // Said out loud because the symptom of a dataset that didn't load is the layer going
                // back to asking Overpass — that is, looking exactly as broken as it used to, with
                // nothing on screen to say why.
                logNetwork(
                    "Parking",
                    when {
                        bytes == null -> "no bundled dataset on this build; falling back to Overpass"
                        dataset == null -> "bundled dataset (${bytes.size} bytes) would not open; falling back to Overpass"
                        else -> "bundled dataset opened (${bytes.size} bytes)"
                    },
                )
            }
            dataset
        }
}
